import { ArrowRight, CircleAlert, CircleCheck, Clock, Cpu, MapPin, Route, Smartphone } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceName, revealsAnything, roundedPosition, wallClock, type RecordedAt } from '@/core/metadata/inspect';
import type { GeoLocation } from '@/core/metadata/location';
import { formatName, type OutputFormatId } from '@/core/project/output-format';
import { formatBytes } from '@/core/time/format';
import { engine } from '@/engine/client';
import { toEngineError } from '@/engine/errors';
import type { InspectionReport } from '@/engine/types';
import { Button, Notice, Spinner } from '../primitives';
import { useErrorMessage } from '../use-error-message';
import { DropTarget } from './IntentBands';

/** More than this many at once is a batch job, not a check. */
const MAX_FILES = 10;

type Inspection = { readonly file: File } & ({ readonly status: 'reading' } | { readonly status: 'done'; readonly report: InspectionReport } | { readonly status: 'error'; readonly message: string });

export interface InspectPanelProps {
  /** The files last dropped or picked anywhere on the page; each new list is checked. */
  readonly files: readonly File[] | null;
  readonly onFiles: (files: File[]) => void;
  /** Save the file again without its details (quick convert to `format`). */
  readonly onClean: (file: File, format: OutputFormatId) => void;
  /** Back to the recipes: the visitor wants to edit, not only check. */
  readonly onEdit: () => void;
  readonly dragging: boolean;
  /** Status text while a clean copy is being opened, or false. */
  readonly loading: string | false;
}

/**
 * The metadata inspector (`/?inspect=1`, the metadata pages' button): what a
 * dropped video says about where, when and on what it was made, read on the
 * device, with a clean copy one tap away.
 */
export function InspectPanel({ files, onFiles, onClean, onEdit, dragging, loading }: InspectPanelProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [inspections, setInspections] = useState<readonly Inspection[]>([]);

  useEffect(() => {
    if (!files?.length) return;
    const chosen = files.slice(0, MAX_FILES);
    setInspections(chosen.map((file) => ({ file, status: 'reading' })));
    const tasks = chosen.map((file, index) => {
      const task = engine.inspect(file);
      task.promise.then(
        (report) => setInspections((current) => current.map((entry, at) => (at === index && entry.file === file ? { file, status: 'done', report } : entry))),
        (error: unknown) => setInspections((current) => current.map((entry, at) => (at === index && entry.file === file ? { file, status: 'error', message: message(toEngineError(error)) } : entry))),
      );
      return task;
    });
    // A newer drop, or leaving the page, stops the reads still running.
    return () => tasks.forEach((task) => task.cancel());
  }, [files, message]);

  const reading = inspections.some((entry) => entry.status === 'reading');
  return (
    <section aria-labelledby="inspect-title" className="border-t border-line-strong pt-4">
      {inspections.length === 0 ? (
        <div className="-mx-3 grid gap-x-8 gap-y-5 rounded-[22px] bg-panel p-4 shadow-float ring-1 ring-line sm:-mx-5 sm:p-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-center">
          <div className="min-w-0">
            <h2 id="inspect-title" className="text-[21px] leading-tight font-semibold tracking-[-0.03em] text-balance text-fg sm:text-[24px]">
              {t('home.inspect.title')}
            </h2>
            <p className="mt-1.5 max-w-120 text-[15px] leading-snug text-pretty text-fg-muted">{t('home.inspect.body')}</p>
          </div>
          <DropTarget dragging={dragging} loading={false} onFiles={onFiles} hint={t('home.inspect.hint')} />
        </div>
      ) : (
        <div className="space-y-4">
          <h2 id="inspect-title" className="sr-only">
            {t('home.inspect.reportsLabel')}
          </h2>
          <div aria-live="polite" aria-busy={reading} className="space-y-4">
            {inspections.map((entry, index) => (
              <ReportCard key={`${index}-${entry.file.name}`} entry={entry} onClean={onClean} />
            ))}
          </div>
          {loading && (
            <p className="flex items-center gap-2.5 px-2 text-sm font-medium text-fg-muted" role="status">
              <Spinner className="text-accent-text" />
              {loading}
            </p>
          )}
          {!reading && !loading && (
            <div className="-mx-3 sm:-mx-5">
              <DropTarget dragging={dragging} loading={false} onFiles={onFiles} hint={t('home.inspect.another')} />
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onEdit}
        className="group mt-4 inline-flex items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-fg-muted transition-colors duration-200 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
      >
        {t('home.inspect.edit')}
        <ArrowRight size={14} className="transition-transform duration-200 ease-out-soft group-hover:translate-x-0.5" />
      </button>
    </section>
  );
}

function ReportCard({ entry, onClean }: { readonly entry: Inspection; readonly onClean: InspectPanelProps['onClean'] }) {
  const { t, i18n } = useTranslation();
  return (
    <article className="-mx-3 rounded-[22px] bg-panel p-4 shadow-float ring-1 ring-line sm:-mx-5 sm:p-6" aria-label={entry.file.name}>
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="min-w-0 truncate text-[17px] font-semibold tracking-[-0.02em] text-fg">{entry.file.name}</h3>
        <span className="text-[13px] text-fg-subtle tabular">{formatBytes(entry.file.size, i18n.language)}</span>
      </header>
      {entry.status === 'reading' && (
        <p className="mt-4 flex items-center gap-2.5 text-sm font-medium text-fg-muted">
          <Spinner className="text-accent-text" />
          {t('home.inspect.reading')}
        </p>
      )}
      {entry.status === 'error' && (
        <div className="mt-4">
          <Notice tone="danger" icon={<CircleAlert size={15} />}>
            {entry.message}
          </Notice>
        </div>
      )}
      {entry.status === 'done' && (
        <>
          <Findings report={entry.report} />
          <div className="mt-5 flex flex-col gap-3 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-110 text-[13px] leading-snug text-pretty text-fg-muted">{t('home.inspect.cleanNote')}</p>
            <Button variant="primary" onClick={() => onClean(entry.file, entry.report.cleanFormat)} className="shrink-0">
              {t('home.inspect.clean', { format: formatName(entry.report.cleanFormat) })}
            </Button>
          </div>
        </>
      )}
    </article>
  );
}

function Findings({ report }: { readonly report: InspectionReport }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const position = (location: GeoLocation) => {
    const rounded = roundedPosition(location);
    const hemisphere = (key: 'N' | 'S' | 'E' | 'W') => t(`home.inspect.hemispheres.${key}`);
    // Not `ns`: i18next reads that option as a namespace.
    return t('home.inspect.position', { latitude: rounded.latitude, longitude: rounded.longitude, northSouth: hemisphere(rounded.ns), eastWest: hemisphere(rounded.ew) });
  };
  const device = deviceName(report);
  const revealed = [
    report.location && t('home.inspect.reveals.location'),
    report.track && t('home.inspect.reveals.track'),
    device && t('home.inspect.reveals.device'),
    report.recorded && t('home.inspect.reveals.recorded'),
  ].filter((item): item is string => Boolean(item));
  const list = new Intl.ListFormat(lang, { type: 'conjunction' }).format(revealed);
  const anything = revealsAnything(report, report.track !== null);

  return (
    <>
      <p className={anything ? 'mt-3 text-[15px] leading-snug font-medium text-pretty text-fg' : 'mt-3 text-[15px] leading-snug text-pretty text-fg-muted'}>
        {anything ? t('home.inspect.found', { list }) : t('home.inspect.none')}
      </p>
      <dl className="mt-4 divide-y divide-line">
        <Row icon={<MapPin size={16} />} label={t('home.inspect.rows.location')} value={report.location && position(report.location)} note={report.location && t('home.inspect.positionNote')} />
        {report.track && (
          <Row
            icon={<Route size={16} />}
            label={t('home.inspect.rows.track')}
            value={t('home.inspect.track', { count: report.track.points, position: position(report.track.first) })}
            note={t('home.inspect.trackNote')}
          />
        )}
        <Row icon={<Smartphone size={16} />} label={t('home.inspect.rows.device')} value={device} />
        <Row icon={<Clock size={16} />} label={t('home.inspect.rows.recorded')} value={report.recorded && recordedText(report.recorded, lang, (when, offset) => t('home.inspect.recordedZone', { when, offset }))} />
        <Row icon={<Cpu size={16} />} label={t('home.inspect.rows.software')} value={report.software} quiet />
      </dl>
      {report.other.length > 0 && (
        <details className="group mt-2">
          <summary className="cursor-pointer list-none rounded-md py-2 text-[13px] font-medium text-fg-muted outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-details-marker]:hidden">
            {t('home.inspect.other', { count: report.other.length })}
          </summary>
          <dl className="grid gap-x-6 gap-y-1.5 pb-1 text-[13px] sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)]">
            {report.other.map((tag) => (
              <div key={tag.key} className="contents">
                <dt className="truncate font-mono text-fg-subtle">{tag.key}</dt>
                <dd className="min-w-0 break-words text-fg-muted">{tag.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </>
  );
}

/** "4 May 2026, 18:21 (UTC+03:00)": the time where it was recorded, when the file says so. */
function recordedText(recorded: RecordedAt, lang: string, withZone: (when: string, offset: string) => string): string {
  const clock = wallClock(recorded);
  const when = `${new Intl.DateTimeFormat(lang, { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${clock.day}T00:00:00Z`))}, ${clock.time}`;
  return clock.offset ? withZone(when, clock.offset) : when;
}

/** One finding: what the file says, or that it says nothing. A quiet row (the software) is not counted as giving anything away. */
function Row({ icon, label, value, note, quiet = false }: { readonly icon: ReactNode; readonly label: string; readonly value: string | null; readonly note?: string | null; readonly quiet?: boolean }) {
  const { t } = useTranslation();
  const present = value !== null;
  return (
    <div className="grid gap-1 py-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-6">
      <dt className="flex items-center gap-2 text-[14px] font-medium text-fg">
        <span className={present && !quiet ? 'text-danger' : 'text-fg-subtle'}>{icon}</span>
        {label}
      </dt>
      <dd className="min-w-0">
        {present ? (
          <>
            <span className="flex items-start gap-2 text-[14px] text-fg">
              {!quiet && <span className="sr-only">{t('home.inspect.inFile')}: </span>}
              <span className="min-w-0 break-words">{value}</span>
            </span>
            {note && <span className="mt-0.5 block text-[12.5px] leading-snug text-fg-subtle">{note}</span>}
          </>
        ) : (
          <span className="flex items-center gap-1.5 text-[14px] text-fg-muted">
            <CircleCheck size={14} className="shrink-0 text-accent-text" />
            {t('home.inspect.notFound')}
          </span>
        )}
      </dd>
    </div>
  );
}
